import React, { useState } from 'react';
import { ChildProfile } from '../../models/childProfile';
import { ChildProfileDataService } from '../../services/childProfileDataService';
import { toDateString, formatDisplayDate } from '../../services/dateUtils';

interface MarkAsLeftModalProps {
    child: ChildProfile;
    /** didDelete = true when the soft delete went through (caller refreshes lists). */
    onClose: (didDelete: boolean) => void;
}

/**
 * Soft-delete ("mark as left") flow, launched from the child edit panel.
 * Two stages: confirm (date + optional reason, with a plain-English
 * explanation of what soft delete does) → result (the computed hard-delete
 * due date returned by the API, so the admin sees the retention outcome of
 * the org's policy before they navigate away).
 */
const MarkAsLeftModal: React.FC<MarkAsLeftModalProps> = ({ child, onClose }) => {
    const [leftAt, setLeftAt] = useState<string>(toDateString(new Date()));
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<{ hardDeleteDueAt: string; anchorUsed: 'dob25' | 'leaving' } | null>(null);

    const childName = `${child.first_name} ${child.last_name}`.trim() || child.initials;

    const handleConfirm = async () => {
        if (!leftAt) {
            setError('Please enter the date the child left.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const r = await ChildProfileDataService.softDelete(child.id, leftAt, reason.trim() || undefined);
            setResult({ hardDeleteDueAt: r.hardDeleteDueAt, anchorUsed: r.anchorUsed });
        } catch (err: any) {
            console.error('Soft delete failed:', err);
            setError(err?.response?.data?.error || 'Failed to mark the child as left.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content ivory-panel">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {result ? 'Child marked as left' : `Mark ${childName} as left`}
                        </h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={() => onClose(!!result)} />
                    </div>

                    {result ? (
                        <>
                            <div className="modal-body">
                                <p>
                                    <strong>{childName}</strong> is now recorded as having left on{' '}
                                    <strong>{formatDisplayDate(leftAt)}</strong>.
                                </p>
                                <ul className="mb-2">
                                    <li>The profile is hidden from posts, schedules, reports and documents.</li>
                                    <li>Any parent/carer accounts linked to this child no longer have access.</li>
                                    <li>
                                        All data is retained until{' '}
                                        <strong>{formatDisplayDate(result.hardDeleteDueAt)}</strong>{' '}
                                        ({result.anchorUsed === 'dob25'
                                            ? "the child's 25th birthday, per your organisation's retention policy"
                                            : "per your organisation's retention policy"}),
                                        when an admin can permanently delete it.
                                    </li>
                                </ul>
                                <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                    You can undo this at any time before permanent deletion from the{' '}
                                    <strong>Leavers</strong> tab.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-primary" onClick={() => onClose(true)}>
                                    OK
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="modal-body">
                                {error && <div className="alert alert-danger py-2">{error}</div>}
                                <p>
                                    This flags <strong>{childName}</strong> as having left the setting:
                                </p>
                                <ul>
                                    <li>The profile and all its data are hidden across the app.</li>
                                    <li>Parent/carer access to this child is removed immediately.</li>
                                    <li>
                                        Nothing is deleted yet — data is kept for your organisation's
                                        retention period, and this action can be undone from the{' '}
                                        <strong>Leavers</strong> tab.
                                    </li>
                                </ul>
                                <div className="mb-3">
                                    <label className="form-label">Date left</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={leftAt}
                                        onChange={e => setLeftAt(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="mb-1">
                                    <label className="form-label">Reason <span className="text-muted">(optional)</span></label>
                                    <textarea
                                        className="form-control"
                                        rows={2}
                                        maxLength={500}
                                        value={reason}
                                        onChange={e => setReason(e.target.value)}
                                        placeholder="e.g. moved to secondary school"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => onClose(false)} disabled={submitting}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-danger" onClick={handleConfirm} disabled={submitting}>
                                    {submitting ? 'Marking as left…' : 'Mark as left'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MarkAsLeftModal;
